import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { LocalStorage } from '@ngx-pwa/local-storage';
import { JwtHelperService } from '@auth0/angular-jwt';
import { BehaviorSubject, of, concat, Observable, forkJoin } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';

import { AuthResponse } from '../models/auth-response';
import { TokenPair } from '../models/token-pair';
import { ConfigService } from 'src/app/services/config.service';

const jwt = new JwtHelperService();

@Injectable({ providedIn: 'root' })
export class AuthService {
  private token = new BehaviorSubject<string>(undefined);
  private user: BehaviorSubject<AuthResponse>;
  private readonly TOKEN_STORAGE_KEY = 'access_token';
  private readonly REFRESH_TOKEN_STORAGE_KEY = 'refresh_token';

  constructor(
    private http: HttpClient,
    private localStorage: LocalStorage,
    private configService: ConfigService
  ) {
    this.localStorage
      .getItem(this.TOKEN_STORAGE_KEY)
      .subscribe((token: string) => {
        this.token.next(token);
      });
    this.user = new BehaviorSubject(null);

    this.getStoredUser().subscribe(user => this.user.next(user));
  }

  public isLogged(): Promise<boolean> {
    return this.getToken().then(async token => {
      if (token && jwt.isTokenExpired(token)) {
        token = await this.refreshToken().toPromise();
      }
      return token !== null;
    });
  }

  public login(username, password): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.configService.apiUrl}/tokens`, {
        username,
        password,
        scope: 'app_web',
      })
      .pipe(
        tap(response => {
          this.localStorage.setItemSubscribe('access_request', response);
          this.saveTokenPair(response.token, response.refreshToken);
        })
      )
      .pipe(tap(response => this.user.next(response)));
  }

  public logout(): Promise<any> {
    const clearTokenPromise = this.localStorage
      .removeItem(this.TOKEN_STORAGE_KEY)
      .toPromise();
    const clearUserPromise = this.localStorage
      .removeItem('access_request')
      .toPromise();
    return Promise.all([clearTokenPromise, clearUserPromise]).then(() => {
      this.token.next(null);
      this.user.next(null);
    });
  }

  public getToken(): Promise<string> {
    let token$;
    if (this.token.value === undefined) {
      token$ = this.localStorage.getItem<string>(this.TOKEN_STORAGE_KEY);
      token$.pipe(tap((token: string) => this.token.next(token)));
    } else {
      token$ = of(this.token.value);
    }
    return token$.toPromise();
  }

  public getUser(): Observable<AuthResponse> {
    return concat(of(this.user.value), this.user.asObservable());
  }

  public getStoredUser(): Observable<AuthResponse> {
    return this.localStorage
      .getItem<AuthResponse>('access_request')
      .pipe(map(response => response as AuthResponse));
  }

  public refreshToken(): Observable<string> {
    return this.getTokenPair().pipe(
      mergeMap(pair => {
        return this.doRefreshToken(pair.token, pair.refreshToken).pipe(
          catchError(() => of({ token: null })),
          map(response => response.token)
        );
      })
    );
  }

  private doRefreshToken(
    token: string,
    refreshToken: string
  ): Observable<AuthResponse> {
    const url = `${this.configService.apiUrl}/RefreshTokens`;
    const params = { token, refreshToken };
    return this.http
      .post<AuthResponse>(url, null, { params })
      .pipe(
        tap(({ token, refreshToken }) => {
          this.saveTokenPair(token, refreshToken);
        })
      );
  }

  private getTokenPair(): Observable<TokenPair> {
    return forkJoin([
      this.localStorage.getItem<string>(this.TOKEN_STORAGE_KEY),
      this.localStorage.getItem<string>(this.REFRESH_TOKEN_STORAGE_KEY),
    ]).pipe(
      // @ts-ignore
      map((tokens: string[]) => {
        return {
          token: tokens[0],
          refreshToken: tokens[1],
        };
      })
    );
  }

  private saveTokenPair(token, refreshToken) {
    this.localStorage.setItemSubscribe(this.TOKEN_STORAGE_KEY, token);
    this.localStorage.setItemSubscribe(
      this.REFRESH_TOKEN_STORAGE_KEY,
      refreshToken
    );
    this.token.next(token);
  }
}
